import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

interface Account { id: string; name: string; region: string; is_active?: number }
interface Campaign {
  id: string; name: string; marketplace: string; type: string; targeting: string;
  state: string; dailyBudget: number; bidStrategy: string; asin: string | null;
  spend: number; sales: number; orders: number; impressions: number; clicks: number;
  acos: number | null; roas: number | null; budgetUtilization: number | null;
}
interface Template {
  id: string; name: string; description: string; isBuiltin: boolean;
  campaigns: Array<{ namePattern: string; targetingType: string; budget: number }>;
}

const MP_NAMES: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES',
};

const TYPE_LABELS: Record<string, string> = {
  sponsoredProducts: 'SP', sponsoredBrands: 'SB', sponsoredDisplay: 'SD', sponsoredVideo: 'SV',
};


export function PpcCampaigns() {
  const [activeAccounts, setActiveAccounts] = useState<Account[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [allMarketplaces, setAllMarketplaces] = useState<string[]>([]);
  const [selectedMp, setSelectedMp] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'spend' | 'sales' | 'acos'>('spend');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  // Inline editing
  const [editingBudget, setEditingBudget] = useState<string | null>(null);
  const [budgetValue, setBudgetValue] = useState('');

  // Create campaign modal
  const [showCreate, setShowCreate] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [products, setProducts] = useState<Array<{ asin: string; sku: string; nickname: string | null; title: string | null }>>([]);
  const [createAsin, setCreateAsin] = useState('');
  const [createTemplate, setCreateTemplate] = useState('');
  const [createMp, setCreateMp] = useState('ATVPDKIKX0DER');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get<Account[]>('/accounts').then(accts => {
      const active = accts.filter(a => a.is_active !== 0);
      setActiveAccounts(active);
      if (active.length === 0) setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadCampaigns = useCallback(async () => {
    if (activeAccounts.length === 0) return;
    setRefreshing(true);
    setError('');
    try {
      const results = await Promise.all(
        activeAccounts.map(a =>
          api.get<{ campaigns: Campaign[] }>(
            `/ppc/campaigns/${a.id}?sort_by=${sortBy}${stateFilter ? `&state=${stateFilter}` : ''}${selectedMp ? `&marketplace=${selectedMp}` : ''}`,
          ).catch(() => ({ campaigns: [] })),
        ),
      );

      const merged = results.flatMap(r => r.campaigns);
      merged.sort((a, b) => {
        if (sortBy === 'acos') return (a.acos ?? 999) - (b.acos ?? 999);
        const av = sortBy === 'spend' ? a.spend : a.sales;
        const bv = sortBy === 'spend' ? b.spend : b.sales;
        return bv - av;
      });
      setCampaigns(merged);

      // Populate marketplace buttons from campaign data (once)
      setAllMarketplaces(prev => {
        if (prev.length > 0) return prev;
        const mpSet = new Set<string>();
        for (const c of merged) if (c.marketplace) mpSet.add(c.marketplace);
        return [...mpSet].sort();
      });
    } catch (e) { setError(String(e)); }
    setRefreshing(false);
    setLoading(false);
  }, [activeAccounts, sortBy, stateFilter, selectedMp]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // ── Actions ─────────────────────────────────────────────────────────────

  async function toggleCampaignState(campaign: Campaign) {
    const newState = campaign.state === 'ENABLED' ? 'PAUSED' : 'ENABLED';
    const accountId = activeAccounts[0]?.id;
    if (!accountId) return;
    setActionMsg('');
    try {
      await api.post(`/ppc/campaigns/${accountId}/action`, {
        campaignId: campaign.id,
        marketplace: campaign.marketplace,
        actionType: newState === 'PAUSED' ? 'pause_campaign' : 'enable_campaign',
        currentValue: campaign.state,
        newValue: newState,
        campaignName: campaign.name,
      });
      setActionMsg(`${campaign.name}: ${campaign.state} → ${newState} (queued)`);
      // Optimistic update
      setCampaigns(prev => prev.map(c => c.id === campaign.id && c.marketplace === campaign.marketplace ? { ...c, state: newState } : c));
    } catch (e) { setError(String(e)); }
  }

  async function saveBudget(campaign: Campaign) {
    const newBudget = parseFloat(budgetValue);
    if (isNaN(newBudget) || newBudget <= 0) return;
    const accountId = activeAccounts[0]?.id;
    if (!accountId) return;
    try {
      await api.post(`/ppc/campaigns/${accountId}/action`, {
        campaignId: campaign.id,
        marketplace: campaign.marketplace,
        actionType: 'adjust_budget',
        currentValue: String(campaign.dailyBudget),
        newValue: String(newBudget),
        campaignName: campaign.name,
      });
      setActionMsg(`${campaign.name}: budget $${campaign.dailyBudget} → $${newBudget} (queued)`);
      setCampaigns(prev => prev.map(c => c.id === campaign.id && c.marketplace === campaign.marketplace ? { ...c, dailyBudget: newBudget } : c));
    } catch (e) { setError(String(e)); }
    setEditingBudget(null);
  }

  async function handleCreateFromTemplate() {
    if (!createAsin || !createTemplate) return;
    setCreating(true);
    try {
      const accountId = activeAccounts[0]?.id;
      const res = await api.post<{ created: number; message: string }>(`/ppc/campaigns/${accountId}/from-template`, {
        templateId: createTemplate, asin: createAsin, marketplace: createMp,
      });
      setActionMsg(res.message);
      setShowCreate(false);
      setCreateAsin('');
    } catch (e) { setError(String(e)); }
    setCreating(false);
  }

  // Load templates + products when create modal opens
  useEffect(() => {
    if (!showCreate) return;
    if (templates.length === 0) {
      api.get<{ templates: Template[] }>('/ppc/campaign-templates')
        .then(r => setTemplates(r?.templates ?? [])).catch(() => {});
    }
    if (products.length === 0) {
      api.get<{ products: Array<{ asin: string; sku: string; nickname: string | null; title: string | null }> }>('/inventory/local-products')
        .then(r => setProducts(r?.products ?? [])).catch(() => {});
    }
  }, [showCreate, templates.length, products.length]);

  const totalSpend = campaigns.reduce((s, c) => s + c.spend, 0);
  const totalSales = campaigns.reduce((s, c) => s + c.sales, 0);
  const totalOrders = campaigns.reduce((s, c) => s + c.orders, 0);
  const overallAcos = totalSales > 0 ? (totalSpend / totalSales) * 100 : null;

  if (loading) return <div className="py-12 text-center text-gray-400">Loading campaigns...</div>;

  return (
    <div className={`space-y-6 ${refreshing ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Campaign Manager {refreshing && <span className="ml-2 text-sm font-normal text-gray-400">Loading...</span>}
          </h1>
          <p className="mt-1 text-sm text-gray-500">View, manage, and create PPC campaigns across all marketplaces</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
          + Create Campaigns
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">{actionMsg}</div>}

      {/* Filters */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          <button onClick={() => setSelectedMp(null)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${!selectedMp ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>All</button>
          {allMarketplaces.map(mp => (
            <button key={mp} onClick={() => setSelectedMp(mp === selectedMp ? null : mp)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${selectedMp === mp ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {MP_NAMES[mp] ?? mp}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value="">All States</option>
            <option value="ENABLED">Enabled</option>
            <option value="PAUSED">Paused</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as 'spend' | 'sales' | 'acos')}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value="spend">Sort: Spend</option>
            <option value="sales">Sort: Sales</option>
            <option value="acos">Sort: ACoS</option>
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Campaigns" value={String(campaigns.length)} />
        <KpiCard label="Spend (7d)" value={`$${totalSpend.toFixed(0)}`} />
        <KpiCard label="Sales (7d)" value={`$${totalSales.toFixed(0)}`} />
        <KpiCard label="Orders (7d)" value={String(totalOrders)} />
        <KpiCard label="ACoS" value={overallAcos !== null ? `${overallAcos.toFixed(1)}%` : '--'}
          color={overallAcos !== null ? (overallAcos > 30 ? 'red' : overallAcos > 20 ? 'yellow' : 'green') : 'gray'} />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-100 bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-center font-medium text-gray-600 w-16">State</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Campaign</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Mkt</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Budget</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 cursor-pointer hover:text-brand-600" onClick={() => setSortBy('spend')}>Spend{sortBy === 'spend' ? ' ↓' : ''}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 cursor-pointer hover:text-brand-600" onClick={() => setSortBy('sales')}>Sales{sortBy === 'sales' ? ' ↓' : ''}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Orders</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 cursor-pointer hover:text-brand-600" onClick={() => setSortBy('acos')}>ACoS{sortBy === 'acos' ? ' ↑' : ''}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">ROAS</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">CTR</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">CPC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {campaigns.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-gray-400">No campaigns found</td></tr>
            )}
            {campaigns.map(c => {
              const key = `${c.id}:${c.marketplace}`;
              const ctr = c.impressions > 0 ? (c.clicks / c.impressions * 100) : 0;
              const cpc = c.clicks > 0 ? c.spend / c.clicks : 0;
              const isEditingBudget = editingBudget === key;

              return (
                <tr key={key} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => toggleCampaignState(c)} title={c.state === 'ENABLED' ? 'Click to pause' : 'Click to enable'}
                      className={`inline-flex h-5 w-9 items-center rounded-full transition-colors ${c.state === 'ENABLED' ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${c.state === 'ENABLED' ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </td>
                  <td className="px-3 py-2 max-w-[220px]">
                    <div className="font-medium text-gray-800 truncate" title={c.name}>{c.name}</div>
                    {c.asin && <div className="text-[10px] text-gray-400">{c.asin}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{MP_NAMES[c.marketplace] ?? c.marketplace}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                      c.type === 'sponsoredBrands' ? 'bg-purple-100 text-purple-700' :
                      c.type === 'sponsoredVideo' ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>{TYPE_LABELS[c.type] ?? c.type}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isEditingBudget ? (
                      <div className="flex items-center justify-end gap-1">
                        <input type="number" value={budgetValue} onChange={e => setBudgetValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveBudget(c); if (e.key === 'Escape') setEditingBudget(null); }}
                          className="w-20 rounded border border-brand-300 px-1.5 py-0.5 text-xs text-right" autoFocus />
                        <button onClick={() => saveBudget(c)} className="text-green-600 text-xs font-medium">Save</button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingBudget(key); setBudgetValue(String(c.dailyBudget ?? '')); }}
                        className="text-gray-600 hover:text-brand-600" title="Click to edit budget">
                        {c.dailyBudget ? `$${c.dailyBudget}` : '--'}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-800">${c.spend.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-gray-700">${c.sales.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{c.orders}</td>
                  <td className={`px-3 py-2 text-right font-medium ${
                    c.acos !== null ? (c.acos > 0.30 ? 'text-red-600' : c.acos > 0.20 ? 'text-yellow-600' : 'text-green-600') : 'text-gray-400'
                  }`}>{c.acos !== null ? `${(c.acos * 100).toFixed(1)}%` : '--'}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{c.roas !== null ? `${c.roas.toFixed(1)}x` : '--'}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{ctr.toFixed(2)}%</td>
                  <td className="px-3 py-2 text-right text-gray-500">${cpc.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create Campaign Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900">Create Campaigns from Template</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Product</label>
                <select value={createAsin} onChange={e => setCreateAsin(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                  <option value="">Select a product...</option>
                  {products.map(p => (
                    <option key={p.asin} value={p.asin}>
                      {p.nickname ?? p.title?.slice(0, 50) ?? p.sku} — {p.asin}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Template</label>
                <select value={createTemplate} onChange={e => setCreateTemplate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                  <option value="">Select a template...</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name} — {t.description}</option>
                  ))}
                </select>
                {createTemplate && templates.find(t => t.id === createTemplate) && (
                  <div className="mt-2 rounded-lg bg-gray-50 p-2 text-xs">
                    {templates.find(t => t.id === createTemplate)!.campaigns.map((c, i) => (
                      <div key={i} className="flex justify-between py-0.5">
                        <span className="text-gray-600">{c.namePattern.replace('{ASIN}', createAsin || '{ASIN}')}</span>
                        <span className="text-gray-500">{c.targetingType} — ${c.budget}/day</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Marketplace</label>
                <select value={createMp} onChange={e => setCreateMp(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                  {Object.entries(MP_NAMES).map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowCreate(false)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreateFromTemplate} disabled={creating || !createAsin || !createTemplate}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Campaigns'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, color }: { label: string; value: string; color?: string }) {
  const textColor = color === 'red' ? 'text-red-600' : color === 'yellow' ? 'text-yellow-600' : color === 'green' ? 'text-green-600' : 'text-gray-900';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${textColor}`}>{value}</p>
    </div>
  );
}
